import { Form } from 'antd';
import type { FormInstance } from 'antd';
import {
  missingCustomerFields,
  STRICT_CUSTOMER_VALIDATION_POLICY,
  type CustomerCheckoutValues,
  type CustomerValidationPolicy,
  type MissingCustomerField,
} from '../utils/customerCheckout';
import type { FulfillmentMode } from '../types';

/**
 * Đủ thông tin khách hàng bắt buộc để ghi hoá đơn chưa — cập nhật live.
 *
 * `<Form onValuesChange>` chỉ bắn khi user gõ/chọn trên một Form.Item đang
 * mount, nên các thao tác ghi thẳng vào form store bằng `setFieldsValue` (chọn
 * khách từ ô gợi ý, nút ✕ xoá khách, đổi loại khách) sẽ không làm POSPage
 * re-render. `Form.useWatch` đăng ký vào watchList của store nên bắt được mọi
 * đường ghi.
 *
 * `preserve: true` => selector đọc `getFieldsValue(true)` (toàn bộ store) thay
 * vì chỉ các field đang mount; các field customer/invoice nằm trong
 * CustomerDialog lazy-mount, chưa mở dialog thì no-arg read sẽ thấy
 * `undefined`.
 *
 * SELECTOR PHẢI LÀ PROJECTION HẸP, không phải `(v) => v` (toàn bộ store, ~36
 * field). `rc-field-form` chỉ `setValue` (gây re-render) khi
 * `JSON.stringify(selectorResult)` đổi — trả cả store nghĩa là BẤT KỲ field
 * nào đổi (kể cả field `isCustomerReady` không đọc, ví dụ `customer_email`,
 * `delivery_note`, `vouchers`...) cũng làm POSPage re-render (bảng sản phẩm,
 * panel, footer) kèm 1 lần stringify cả store — lag gõ phím trên máy POS yếu.
 * Object literal dưới đây chỉ liệt kê đúng field mà `isCustomerReady` đọc.
 *
 * RÀNG BUỘC khi sửa file này:
 * (a) KHÔNG đưa `fulfillmentMode` vào bên trong selector của `Form.useWatch`
 *     (kiểu `(v) => isCustomerReady(v, fulfillmentMode)`). `rc-field-form`
 *     đăng ký watcher trong `useEffect` với deps thực chất rỗng, nên closure
 *     của selector chỉ được chụp MỘT LẦN lúc mount — mọi lần re-render sau đó
 *     với `fulfillmentMode` mới vẫn chạy selector CŨ, kẹt giá trị
 *     `fulfillmentMode` lúc mount vĩnh viễn (stale closure). Cách đúng: watch
 *     giá trị thô của form, rồi tính `isCustomerReady` ở NGOÀI selector, tại
 *     thân hàm — mỗi lần component render lại đều dùng `fulfillmentMode` mới
 *     nhất truyền vào tham số.
 * (b) Projection trong selector PHẢI phủ ĐỦ mọi field mà `isCustomerReady`
 *     (xem `../utils/customerCheckout.ts`) đọc. Nếu thêm field mới vào
 *     `isCustomerReady`, PHẢI thêm field đó vào object literal bên dưới —
 *     thiếu field sẽ tái sinh bug stale: cổng không cập nhật khi field đó đổi.
 */
/**
 * Các trường khách hàng còn trống — dùng để dựng toast lỗi khi bấm "Ghi hoá đơn
 * và thanh toán", và để biết có cần mở CustomerDialog cho antd validate/tô đỏ
 * các field lazy-mount hay không.
 */
export function useMissingCustomerFields(
  form: FormInstance,
  fulfillmentMode: FulfillmentMode = 'INSTANT',
  validationPolicy: CustomerValidationPolicy = STRICT_CUSTOMER_VALIDATION_POLICY,
): MissingCustomerField[] {
  const gate = Form.useWatch(
    (v: CustomerCheckoutValues) => ({
      user_type: v.user_type,
      customer_phone: v.customer_phone,
      citizen_id: v.citizen_id,
      customer_city_id: v.customer_city_id,
      // Ràng buộc (b) ở trên, lần thứ hai bị vi phạm: `missingCustomerFields`
      // thêm hai field này (899df64f) mà projection không theo — `gate` luôn trả
      // `undefined` nên cổng báo thiếu Phường/Xã + Địa chỉ chi tiết kể cả khi
      // thu ngân đã điền, chặn ghi đơn POS vĩnh viễn.
      customer_ward_id: v.customer_ward_id,
      customer_address: v.customer_address,
      care_staff_id: v.care_staff_id,
      receive_method: v.receive_method,
      pickup_store_id: v.pickup_store_id,
      delivery_name: v.delivery_name,
      delivery_phone: v.delivery_phone,
      invoice_name: v.invoice_name,
      invoice_tax_id: v.invoice_tax_id,
      invoice_address: v.invoice_address,
      // TCR-2618 — `buyer_cccd` (CCCD người mua) KHÔNG còn là trường bắt buộc
      // nên `missingCustomerFields` không đọc nó ⇒ không cần watch ở đây.
      delivery_days: v.delivery_days,
      // Task 25 fix (lịch sử, KHÔNG còn đúng nguyên văn): `missingCustomerFields`
      // (customerCheckout.ts, commit c7cfc29) từng thêm luật "giao hàng bắt
      // buộc chọn shipping_mode" nhưng projection ở đây chưa cập nhật theo —
      // đúng lỗi mà (b) ở trên cảnh báo, khiến `gate.shipping_mode` luôn
      // `undefined` và mọi đơn giao hàng bị chặn tạo đơn vĩnh viễn lúc đó.
      //
      // Task 7b đã XOÁ hẳn luật đó: `missingCustomerFields` không còn bao giờ
      // báo thiếu `shipping_mode` — biên đọc (`effectiveShippingMode` trong
      // `fulfillmentChoice.ts`) coi field rỗng là `'self'` thay vì chặn. Luật
      // thật nay nằm ở biên đọc đó, KHÔNG còn ở đây. Watch field này trong
      // projection giờ là THỪA (không field nào đọc `gate.shipping_mode` để
      // quyết định ready/not-ready nữa) nhưng VÔ HẠI — giữ lại chỉ để khỏi phải
      // sửa lại object literal, không phải vì còn cần.
      shipping_mode: v.shipping_mode,
      // `shipping_partner` / `package_weight` KHÔNG có ở đây: chúng thuộc
      // `missingCarrierFields`. Hook này chỉ dùng để quyết định có mở
      // CustomerDialog hay không — hai ô đó nằm ngoài dialog (cột ĐVVC + đáy cột
      // hàng), kéo vào đây sẽ bật dialog cho thứ dialog không sửa được.
    }),
    { form, preserve: true },
  );
  return missingCustomerFields(gate ?? {}, fulfillmentMode, validationPolicy);
}

export function useCustomerReady(
  form: FormInstance,
  fulfillmentMode: FulfillmentMode = 'INSTANT',
  validationPolicy: CustomerValidationPolicy = STRICT_CUSTOMER_VALIDATION_POLICY,
): boolean {
  return useMissingCustomerFields(form, fulfillmentMode, validationPolicy).length === 0;
}
